import React, { useMemo, useRef } from 'react';
import Autocomplete, {
  type AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import { isEqual } from 'lodash';
import type { FilterOption } from '../../types/common';
import { useRegisterFilterActivity } from '../FilterDrawer/FilterActivityContext';

interface MultiSelectFilterProps {
  label?: string;
  placeholder?: string;
  options: Array<FilterOption>;
  value: Array<string | number>;
  onChange: (selected: Array<string | number>) => void;
  sx?: SxProps<Theme>;
  testId?: string;
  /**
   * When provided, this filter self-reports its active state to the
   * surrounding `FilterDrawerContainer`'s activity registry. Active means
   * at least one option is selected.
   */
  filterId?: string;
  /**
   * Optional override for the auto-derived active state. When omitted, the
   * filter reports `value.length > 0`.
   */
  isActive?: boolean;
  /**
   * Optional callback fired when the dropdown closes with a changed selection.
   * Receives the currently selected options.
   */
  onApply?: (selectedOptions: FilterOption[]) => void;
  /**
   * Optional callback fired when the user clicks the X (clear) icon.
   * Distinct from a plain `onChange([])` because it captures an explicit
   * clear action (useful for analytics).
   */
  onClear?: () => void;
}

/**
 * Reusable multi-select dropdown built on MUI Autocomplete.
 *
 * Behaviour mirrors kitman-frontend's `GridFilterAutocompleteMulti`:
 *   - comma-joined ellipsised tag text in the input
 *   - tooltip on hover showing the full selection
 *   - checkboxed option rows, dropdown stays open across toggles
 *   - placeholder hides when a selection is present
 *
 * Supports both string and numeric IDs.
 */
const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  sx,
  testId,
  filterId,
  isActive,
  onApply,
  onClear,
}) => {
  useRegisterFilterActivity(filterId, isActive ?? value.length > 0);

  const selectedOptions = useMemo(
    () =>
      options.filter((option) => value.includes(option.id as string | number)),
    [options, value]
  );

  // Per-session analytics state: `valueAtOpen` to diff at close, `cleared`
  // so onClose skips the apply event when the X was clicked.
  const sessionRef = useRef({
    valueAtOpen: value as ReadonlyArray<string | number>,
    cleared: false,
  });

  const asText = (options: FilterOption[]) =>
    options.map((option) => option.name).join(', ');

  const tooltipText = asText(selectedOptions);

  return (
    <Autocomplete
      multiple
      fullWidth
      disableCloseOnSelect
      options={options}
      value={selectedOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onOpen={() => {
        sessionRef.current = { valueAtOpen: value, cleared: false };
      }}
      onClose={() => {
        const { valueAtOpen, cleared } = sessionRef.current;
        if (cleared) return;
        if (onApply && !isEqual(value, valueAtOpen)) {
          onApply(selectedOptions);
        }
      }}
      onChange={(
        _,
        newValue: Array<FilterOption>,
        reason: AutocompleteChangeReason
      ) => {
        onChange(newValue.map((option) => option.id));
        if (reason === 'clear') {
          sessionRef.current.cleared = true;
          onClear?.();
        }
      }}
      renderTags={(options) => (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: '3 0 0',
          }}
        >
          {asText(options)}
        </span>
      )}
      renderInput={(params) => (
        <Tooltip title={tooltipText}>
          <TextField
            {...params}
            label={label}
            placeholder={tooltipText ? '' : placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                sx: { flexWrap: 'nowrap' },
              },
            }}
          />
        </Tooltip>
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...liProps } =
          props as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
        return (
          <Stack
            key={key}
            {...liProps}
            component="li"
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <Checkbox checked={selected} />
            {option.name}
          </Stack>
        );
      }}
      sx={sx}
      data-testid={testId}
    />
  );
};

export default MultiSelectFilter;
